"use client";

import { useRouter } from "next/navigation";
import { ItemRequest } from "@/lib/types";
import { formatPrice, formatRelativeTime } from "@/lib/utils";
import { MapPinIcon, ClockIcon } from "@heroicons/react/24/outline";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { buildSellFromRequestHref } from "@/lib/item-requests";

interface ItemRequestPreviewModalProps {
  request: ItemRequest | null;
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to get urgency badge color and text
const getUrgencyBadge = (urgency: string) => {
  switch (urgency) {
    case "high":
      return { color: "bg-red-100 text-red-800", text: "Urgently needed" };
    case "medium":
      return { color: "bg-yellow-100 text-yellow-800", text: "Needed soon" };
    case "low":
      return { color: "sio-badge-positive", text: "No Rush" };
    default:
      return { color: "bg-secondary text-foreground", text: urgency };
  }
};

// Helper function to get status badge color and text
const getStatusBadge = (status: string) => {
  switch (status) {
    case "active":
      return { color: "sio-badge-positive", text: "Active" };
    case "fulfilled":
      return { color: "bg-blue-100 text-blue-800", text: "Fulfilled" };
    case "expired":
      return { color: "bg-secondary text-foreground", text: "Expired" };
    default:
      return { color: "bg-secondary text-foreground", text: status };
  }
};

const formatExpiryDate = (expiresAt?: Date) => {
  if (!expiresAt) return "No expiry";

  const now = new Date();
  const diffInDays = Math.ceil(
    (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays < 0) {
    return "Expired";
  } else if (diffInDays === 0) {
    return "Expires today";
  } else if (diffInDays === 1) {
    return "Expires tomorrow";
  } else {
    return `Expires in ${diffInDays} days`;
  }
};

export default function ItemRequestPreviewModal({
  request,
  isOpen,
  onClose,
}: ItemRequestPreviewModalProps) {
  const router = useRouter();

  if (!request) return null;

  const handleSellItem = () => {
    router.push(buildSellFromRequestHref(request));
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Item Request Details"
      maxWidth="2xl"
      hideFooter
    >
      <div className="space-y-6">
        {/* Header with badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${getStatusBadge(request.status).color}`}
          >
            {getStatusBadge(request.status).text}
          </span>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${getUrgencyBadge(request.urgency).color}`}
          >
            {getUrgencyBadge(request.urgency).text}
          </span>
        </div>

        {/* Title */}
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {request.title}
          </h2>
          <div className="flex items-center text-sm text-text-light">
            <ClockIcon className="h-4 w-4 mr-1" />
            <span>{formatRelativeTime(request.createdAt.toISOString())}</span>
          </div>
        </div>

        {/* Description */}
        {request.description && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">
              Description
            </h3>
            <p className="text-sm text-text-light whitespace-pre-wrap">
              {request.description}
            </p>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Max Price */}
          {request.maxPrice && (
            <div className="bg-secondary rounded-lg p-4">
              <h3 className="text-xs font-semibold text-text-light mb-1">
                Maximum Price
              </h3>
              <p className="text-xl font-bold text-primary">
                {formatPrice(request.maxPrice)}
              </p>
            </div>
          )}

          {/* Location */}
          <div className="bg-secondary rounded-lg p-4">
            <h3 className="text-xs font-semibold text-text-light mb-1">
              Location
            </h3>
            <div className="flex items-center text-foreground">
              <MapPinIcon className="h-4 w-4 mr-1 text-primary flex-shrink-0" />
              <span className="text-sm">{request.userLocation}</span>
            </div>
          </div>

          {/* Category */}
          <div className="bg-secondary rounded-lg p-4">
            <h3 className="text-xs font-semibold text-text-light mb-1">
              Category
            </h3>
            <p className="text-sm text-foreground">{request.category}</p>
          </div>

          {/* Expiry */}
          <div className="bg-secondary rounded-lg p-4">
            <h3 className="text-xs font-semibold text-text-light mb-1">
              Expires
            </h3>
            <p className="text-sm text-foreground">
              {formatExpiryDate(request.expiresAt)}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button onClick={handleSellItem} variant="primary" className="flex-1">
            Sell This Item
          </Button>
          <Button onClick={onClose} variant="secondary" className="flex-1">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
